import { replaceRoute } from '../../../lib/router.js';
import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { Button, Col, Grid, Row } from 'react-bootstrap';
import { Form } from 'react-final-form';
import { InfoModal } from '../../../components/modals/InfoModal.jsx';
import OrgChangeImpactTypesSettingForm from '../../../components/organizations/Settings/OrgChangeImpactTypesSettingForm.jsx';
import SubHeader from '../../../components/SubHeader.jsx';
import { getOrgTermsContent } from '../../../content/utils.js';
import {
  retrieveChangeImpactTypes,
  selectChangeImpactTypes,
  updateChangeImpactTypes,
} from '../../../modules/changeImpactTypes.js';
import { retrieveProject, updateProject } from '../../../modules/projects.js';
import { mapFinalFormErrors } from '../../../lib/utils.js';
import { selectUser as selectCurrentUser } from '../../../modules/auth.js';
import { selectUser } from '../../../modules/users.js';
import CustomTooltip from '../../../components/CustomTooltip.jsx';
import QuestionBlueIcon from '../../../components/icons/QuestionBlueIcon.jsx';
import OrganizationHeader from '../../../components/headers/PageHeaders/Organization/OrganizationHeader.jsx';
import { customProjectStatusesValidate } from '../utils/stakeholdersStructure.js';

const mapErrors = mapFinalFormErrors('Failed to save');

const mapStateToProps = (state, ownProps) => ({
  userId: ownProps.userId,
  currentUser: selectCurrentUser(state),
  user: selectUser(ownProps.userId)(state),
  changeImpactTypes: selectChangeImpactTypes(state),
});

const mapDispatchToProps = {
  retrieveProject,
  updateProject,
  updateChangeImpactTypes,
  retrieveChangeImpactTypes,
};

const OrgCustomChangeImpactTypesFormContainer = (props) => {
  const {
    slug,
    changeImpactTypes,
    retrieveChangeImpactTypes,
    updateChangeImpactTypes,
  } = props;

  const [columns, setColumns] = useState(null);
  const [showLimitInfoModal, setShowLimitInfoModal] = useState(false);

  const randomId = () => Math.random().toString(36).slice(2, 7);
  const redirectToSettings = () => replaceRoute('organization/custom-settings');

  const onSubmit = (values) => {
    const underscoreFilter = () =>
      columns.map((column) => {
        column.name = column.name.replace(/_/g, '-');
        return column;
      });

    const submitPromise = updateChangeImpactTypes({
      change_impact_types: underscoreFilter(),
    });

    return submitPromise
      .then(({ result }) => {
        redirectToSettings();
        retrieveChangeImpactTypes();
      })
      .catch(mapErrors);
  };

  const validate = (values) => customProjectStatusesValidate(values, columns);

  const reorder = (list, startIndex, endIndex) => {
    const result = Array.from(list);
    const [removed] = result.splice(startIndex, 1);
    result.splice(endIndex, 0, removed);

    return result.map((item, index) => ({ ...item, priority: index }));
  };

  const onDragEnd = (result) => {
    if (!result.destination) return;
    const items = reorder(
      columns,
      result.source.index,
      result.destination.index
    );
    setColumns(items);
  };

  const onCreateColumn = () => {
    if (columns.length < 15) {
      setColumns([
        ...columns,
        {
          id: null,
          editor: false,
          priority: columns.length,
          name: '',
          allowDelete: true,
          allowEditor: true,
          draggableId: randomId(),
          tableShow: true,
        },
      ]);
    } else {
      setShowLimitInfoModal(true);
    }
  };

  const onDeleteColumn = (draggableId) => {
    if (
      window.confirm(
        `${getOrgTermsContent('deleteChangeImpactType')} \n\n${getOrgTermsContent('deleteChangeImpactTypeDescription')}`
      )
    ) {
      const filteredColumns = columns.filter(
        (column) => column.draggableId !== draggableId
      );
      setColumns(filteredColumns);
    }
  };

  const onColumnNameChange = (value, itemIndex) => {
    setColumns(
      columns.map((item, index) =>
        itemIndex === index ? { ...item, name: value } : item
      )
    );
  };

  const onContentTypeChange = (itemIndex) => {
    if (
      columns[itemIndex].editor &&
      columns[itemIndex].id &&
      !window.confirm(
        'Are you sure you want to change the field type? Text formatting will be lost.'
      )
    )
      return;

    setColumns(
      columns.map((item, index) =>
        itemIndex === index ? { ...item, editor: !item.editor } : item
      )
    );
  };

  const onTableShowChange = (itemIndex) => {
    setColumns(
      columns.map((item, index) =>
        itemIndex === index ? { ...item, tableShow: !item.tableShow } : item
      )
    );
  };

  useEffect(() => {
    retrieveChangeImpactTypes();
  }, []);

  useEffect(() => {
    changeImpactTypes &&
      setColumns(
        changeImpactTypes
          .map((item) => ({
            ...item,
            draggableId: item.draggableId || randomId(),
            allowDelete: true,
          }))
          .sort((a, b) => a.priority - b.priority)
      );
  }, [changeImpactTypes]);

  return (
    <React.Fragment>
      <InfoModal
        show={showLimitInfoModal}
        onHide={() => setShowLimitInfoModal(false)}
        title="Custom Change Impact Types Limit Reached"
        message={
          <React.Fragment>
            <p className="text-muted">
              You’ve reached the limit for custom change impact types in
              account. You can optimize types list via renaming or deleting
              type(s).
            </p>
            <p className="text-muted">
              If you would like to add more capabilities to your account, please
              contact your ChangeSync Account Manager.
            </p>
          </React.Fragment>
        }
      />
      <OrganizationHeader slug={slug} />
      <SubHeader flexJustify small>
        <div className="flex row">
          <h2 className="m-r-10 m-l-16">Edit Change Impact Types </h2>

          <div className="flex row m-l-16">
            <strong className="bordered text-muted">
              Total:&nbsp;
              <span className="count">
                {columns ? columns.length : ''}
                /15
              </span>
            </strong>
            <CustomTooltip
              tooltip={
                <div style={{ maxWidth: '300px' }}>
                  <strong>
                    <h5
                      style={{
                        fontSize: '14px',
                        fontWeight: '600',
                        lineHeight: '20px',
                        color: '#153142',
                      }}
                      className="m-b-8"
                    >
                      Change Impact Types
                    </h5>
                  </strong>
                  <p
                    style={{
                      fontSize: '12px',
                      fontWeight: '400',
                      lineHeight: '20px',
                      color: '#7F8FA4',
                    }}
                    className="m-b-0 text-muted"
                  >
                    {getOrgTermsContent('changeImpactsLimitMess')}
                  </p>
                </div>
              }
              id="tooltip"
            >
              <Button
                aria-label="View Support for Change modal"
                type="button"
                className="flex flex-align-center m-l-8 btn-last-update-icon"
              >
                <QuestionBlueIcon />
              </Button>
            </CustomTooltip>
          </div>
        </div>
      </SubHeader>

      <Grid>
        <Row>
          <Col sm={12}>
            <div className="component-xl component-bg-white component-xl-padding-vertical component-no-horizontal-padding">
              <Row>
                <Col sm={10} smOffset={1}>
                  {columns ? (
                    <Form
                      component={OrgChangeImpactTypesSettingForm}
                      onSubmit={onSubmit}
                      validate={validate}
                      onDragEnd={onDragEnd}
                      onCreateColumn={onCreateColumn}
                      onColumnNameChange={onColumnNameChange}
                      onDeleteColumn={onDeleteColumn}
                      onContentTypeChange={onContentTypeChange}
                      onTableShowChange={onTableShowChange}
                      // initialValues={initial}
                      data={columns}
                      slug={slug}
                    />
                  ) : null}
                </Col>
              </Row>
            </div>
          </Col>
        </Row>
      </Grid>
    </React.Fragment>
  );
};

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(OrgCustomChangeImpactTypesFormContainer);
